// Mirrors apps/angular/src/js/ng/common/services/sitter-object-service/sitter-object-service.js
// goToSearch / getPrevious / getNext / hasPrevious / hasNext. The Angular
// service was a singleton that held the search results in memory; our React
// bundles are separate pages, so we persist the context in sessionStorage
// under a known key. The Sitters page writes it on every successful fetch,
// and the sitter profile page reads it to render the "Back to search |
// Previous | Next" strip in the header.

const STORAGE_KEY = 'us.sitterSearchNav';

export interface SitterSearchNavEntry {
  id: number;
}

export interface SitterSearchNavState {
  // List of sitter IDs in the order they were shown in search.
  ids: number[];
  // URLSearchParams-encoded filter that produced this list.
  query: string;
  // Zero-based index of the sitter currently in the list that we're viewing.
  // Not mutated across navigation — derived from the current profile id.
  page: number;
  pageCount: number;
  firstPage: boolean;
  lastPage: boolean;
  updated: number;
}

export const saveSitterSearchNav = (state: Omit<SitterSearchNavState, 'updated'>): void => {
  try {
    const payload: SitterSearchNavState = { ...state, updated: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage can throw in private browsing / quota-exceeded — safe to ignore
  }
};

export const readSitterSearchNav = (): SitterSearchNavState | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SitterSearchNavState;
  } catch {
    return null;
  }
};

export const clearSitterSearchNav = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // safe to ignore
  }
};

export const indexOfSitter = (state: SitterSearchNavState | null, sitterId: number): number => {
  if (!state) return -1;
  return state.ids.indexOf(sitterId);
};

export const hasPrevious = (state: SitterSearchNavState | null, index: number): boolean => {
  if (!state) return false;
  if (index > 0) return true;
  return !state.firstPage;
};

export const hasNext = (state: SitterSearchNavState | null, index: number): boolean => {
  if (!state) return false;
  if (index > -1 && index < state.ids.length - 1) return true;
  return !state.lastPage;
};

export const buildSearchHref = (state: SitterSearchNavState | null, currentSitterId: number): string => {
  const params = new URLSearchParams(state?.query ?? '');
  if (state) {
    params.set('page', String(state.page));
    params.set('lastSitterId', String(currentSitterId));
  }
  const qs = params.toString();
  return `/app/sitters${qs ? `?${qs}` : ''}`;
};
